/**
 * Folds a voice session's transcript events into a chat message list.
 *
 * Interim speech-to-text results reach the browser as separate completed
 * streams, each flagged final, so a single utterance re-arrives as a growing
 * prefix of itself ("Una", then "Una orden"). Appending each one blindly is what
 * made voice threads look duplicated and out of order.
 *
 * Events are matched to an existing message by transcript segment id, falling
 * back to prefix matching within an uninterrupted run of user speech. The agent
 * speaking closes the caller's turn, so a caller who genuinely repeats
 * themselves after a reply still gets a separate message.
 *
 * Pure and stateful-by-instance so it can be exercised without a DOM. The
 * caller owns rendering; nothing here talks to the network.
 */

export interface VoiceFoldMessage {
  id: string;
  role: string;
  content?: string;
  parts?: Array<{ type: string; text?: string }>;
}

/** A voice call's turns are bounded; this only guards against a stuck session. */
const MAX_TRACKED_SEGMENTS = 200;

function buildMessage(id: string, role: 'user' | 'assistant', text: string): VoiceFoldMessage {
  // Renderers read `parts` first and fall back to `content`; supply both.
  return { id, role, content: text, parts: [{ type: 'text', text }] };
}

export interface VoiceTranscriptFolder {
  /**
   * Applies a finalized user transcript. Returns the same array reference when
   * the event carries nothing to show, so React can skip the update.
   */
  user(messages: VoiceFoldMessage[], text: string, segmentId?: string): VoiceFoldMessage[];
  /** Applies a finalized agent response and closes the open user turn. */
  agent(messages: VoiceFoldMessage[], text: string, segmentId?: string): VoiceFoldMessage[];
}

export function createVoiceTranscriptFolder(
  newId: (role: 'user' | 'assistant', segmentId?: string) => string = defaultId
): VoiceTranscriptFolder {
  const segmentMessageIds = new Map<string, string>();
  let openUserTurn: { id: string; text: string } | null = null;

  const remember = (segmentId: string | undefined, messageId: string): void => {
    if (!segmentId) return;
    if (segmentMessageIds.size >= MAX_TRACKED_SEGMENTS) segmentMessageIds.clear();
    segmentMessageIds.set(segmentId, messageId);
  };

  const replace = (
    messages: VoiceFoldMessage[],
    id: string,
    role: 'user' | 'assistant',
    text: string
  ): VoiceFoldMessage[] =>
    messages.map((message) => (message.id === id ? buildMessage(id, role, text) : message));

  return {
    user(messages, rawText, segmentId) {
      const text = rawText.trim();
      if (!text) return messages;

      const knownId = segmentId ? segmentMessageIds.get(segmentId) : undefined;
      const stillPresent = (id: string) => messages.some((message) => message.id === id);

      const continuesOpenTurn =
        !knownId &&
        openUserTurn !== null &&
        stillPresent(openUserTurn.id) &&
        (text.startsWith(openUserTurn.text) || openUserTurn.text.startsWith(text));

      const targetId =
        knownId && stillPresent(knownId)
          ? knownId
          : continuesOpenTurn
            ? openUserTurn!.id
            : null;

      if (targetId) {
        remember(segmentId, targetId);
        openUserTurn = { id: targetId, text };
        return replace(messages, targetId, 'user', text);
      }

      const id = newId('user', segmentId);
      remember(segmentId, id);
      openUserTurn = { id, text };
      return [...messages, buildMessage(id, 'user', text)];
    },

    agent(messages, rawText, segmentId) {
      const text = rawText.trim();
      if (!text) return messages;

      // The agent speaking ends the caller's turn regardless of what we render.
      openUserTurn = null;

      const knownId = segmentId ? segmentMessageIds.get(segmentId) : undefined;
      if (knownId && messages.some((message) => message.id === knownId)) {
        return replace(messages, knownId, 'assistant', text);
      }

      const id = newId('assistant', segmentId);
      remember(segmentId, id);
      return [...messages, buildMessage(id, 'assistant', text)];
    },
  };
}

function defaultId(role: 'user' | 'assistant', segmentId?: string): string {
  const suffix = segmentId ?? `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  return `voice-${role === 'user' ? 'user' : 'agent'}-${suffix}`;
}
